/**
 * Reason Field Component (v3 — Append-Mode Workflow Log)
 * 
 * Displays Quick Reason Chips fetched from the Admin API, an editable
 * textarea for NEW notes, and a read-only chronological history of
 * previous notes below.
 * 
 * Behavior:
 * - Chips are fetched from /api/quick-chips?category=pending|clarification
 * - Clicking a chip APPENDS the resolved template to the NEW note textarea
 * - Previous notes are displayed read-only in reverse chronological order
 * - Submitting always APPENDS a new entry (backend $push)
 * - Placeholders auto-resolve from jurisdiction/order context
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FileText, Info, Check, X, Zap, Trash2, AlertTriangle } from 'lucide-react';
import { applyTemplate } from '../../../constants/reasonTemplates';
import { formatDateTimeShortET } from '../../../utils/formatters';
import { validateParcelsForFax, validateParcelsForEmail } from '../../../utils/parcelValidation';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export const StatusReasonField = ({
  selectedStatus,
  reason,
  onReasonChange,
  validationError,
  isReadOnly = false,
  clearValidationErrors,
  jurisdictionData = {},
  statusNotes = [],
  editMode = true,
  activeTab = 'contact',
  onChipIdsChange = null,
  userRole = '',
  orderData = null,
  // TAX-EMAIL-APN-GATE-001: live certificate parcels (take precedence over orderData)
  liveParcels = null,
}) => {
  const [selectedChipIds, setSelectedChipIds] = useState(new Set());
  const [chips, setChips] = useState([]);
  const [chipsLoaded, setChipsLoaded] = useState(false);
  const fetchedCategory = useRef(null);
  const prevStatusRef = useRef(selectedStatus);
  
  // Direct-fetch fax from master jurisdiction — the single source of truth.
  const [masterFax, setMasterFax] = useState('');
  const masterFaxFetched = useRef(false);
  
  // Identify fax chip by either legacy string ID or label (DB chips use UUIDs)
  const isFaxChip = (chip) => chip.id === 'fax_sent' || (chip.label || '').toLowerCase().replace(/\s+/g, '_') === 'fax_sent';
  // TAX-EMAIL-APN-GATE-001: Email chip identified by its stable automation marker (never the label)
  const isEmailChip = (chip) => chip.automation === 'tax_collector_email';
  
  useEffect(() => {
    if (!selectedStatus) {
      masterFaxFetched.current = false;
      setMasterFax('');
      return;
    }
    
    const jName = jurisdictionData?.jurisdiction_name || jurisdictionData?.county || '';
    const propFax = jurisdictionData?.fax || jurisdictionData?.tax_collector_fax || '';
    
    if (propFax) {
      setMasterFax(propFax);
      masterFaxFetched.current = true;
      return;
    }
    
    if (!jName || masterFaxFetched.current) return;
    
    const fetchMasterFax = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API}/jurisdictions?search=${encodeURIComponent(jName)}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          const match = data?.jurisdictions?.[0];
          if (match?.tax_collector_fax) {
            setMasterFax(match.tax_collector_fax);
          }
        }
      } catch (err) {
        console.warn('[ReasonField] Failed to fetch master jurisdiction fax:', err);
      } finally {
        masterFaxFetched.current = true;
      }
    };
    fetchMasterFax();
  }, [selectedStatus, jurisdictionData]);
  
  const effectiveJurisdictionData = useMemo(() => {
    const propFax = jurisdictionData?.fax || jurisdictionData?.tax_collector_fax || '';
    const fax = propFax || masterFax;
    return {
      ...jurisdictionData,
      fax: fax,
      tax_collector_fax: fax
    };
  }, [jurisdictionData, masterFax]);

  // Fetch chips from API when status changes
  useEffect(() => {
    if (selectedStatus !== 'pending' && selectedStatus !== 'clarification') return;
    if (fetchedCategory.current === selectedStatus && chipsLoaded) return;

    const fetchChips = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/quick-chips?category=${selectedStatus}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          let data = await res.json();
          // Fax chip restricted to tax department roles only
          const FAX_ALLOWED_ROLES = ['admin', 'tax_search_team_leader', 'tax_processor', 'processor'];
          const role = (userRole || '').toLowerCase();
          if (!FAX_ALLOWED_ROLES.includes(role)) {
            data = data.filter(chip => chip.id !== 'fax_sent' && !(chip.label || '').toLowerCase().includes('fax'));
          }
          setChips(data);
        }
      } catch {
        // Silent fail — chips are optional
      } finally {
        setChipsLoaded(true);
        fetchedCategory.current = selectedStatus;
      }
    };
    fetchChips();
  }, [selectedStatus, chipsLoaded]);

  // Reset chips when switching between different statuses
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    setSelectedChipIds(new Set());
    setChipsLoaded(false);
    fetchedCategory.current = null;
    if (prevStatus && prevStatus !== selectedStatus && selectedStatus) {
      onReasonChange('');
    }
    prevStatusRef.current = selectedStatus;
  }, [selectedStatus, onReasonChange]);

  // Reset chips when exiting edit mode (user toggled off the active status)
  const prevEditModeRef = useRef(editMode);
  useEffect(() => {
    if (prevEditModeRef.current && !editMode) {
      setSelectedChipIds(new Set());
    }
    prevEditModeRef.current = editMode;
  }, [editMode]);

  const hasJurisdiction = useMemo(() =>
    !!(jurisdictionData?.county || jurisdictionData?.jurisdiction_name),
    [jurisdictionData?.county, jurisdictionData?.jurisdiction_name]
  );

  const faxValue = effectiveJurisdictionData?.fax || effectiveJurisdictionData?.tax_collector_fax || '';
  const prevFaxRef = useRef(faxValue);

  useEffect(() => {
    const prevFax = prevFaxRef.current;
    prevFaxRef.current = faxValue;
    if (!prevFax && faxValue && selectedChipIds.size > 0 && chips.length > 0) {
      const selectedChipsList = chips.filter(c => selectedChipIds.has(c.id));
      if (selectedChipsList.length > 0) {
        const rebuilt = selectedChipsList
          .map(chip => applyTemplate(chip.template, effectiveJurisdictionData))
          .join('\n\n');
        onReasonChange(rebuilt);
      }
    }
  }, [faxValue, selectedChipIds, chips, effectiveJurisdictionData, onReasonChange]);

  // Parcel/APN validation for fax chip (hook kept above the early return
  // for rules-of-hooks compliance)
  const parcelCheck = useMemo(() => validateParcelsForFax(orderData), [orderData]);
  const isFaxChipBlocked = !parcelCheck.valid;
  const emailParcelCheck = useMemo(() => validateParcelsForEmail(orderData, liveParcels), [orderData, liveParcels]);
  const isEmailChipBlocked = !emailParcelCheck.valid;
  const blockedMessage = (chip) => (isFaxChip(chip) && isFaxChipBlocked ? parcelCheck.message
    : isEmailChip(chip) && isEmailChipBlocked ? emailParcelCheck.message : null);
  const notifyChipIds = (next) => {
    if (!onChipIdsChange) return;
    const ids = Array.from(next);
    onChipIdsChange(ids, { hasEmailAutomation: chips.some(c => ids.includes(c.id) && isEmailChip(c)) });
  };

  if (selectedStatus !== 'pending' && selectedStatus !== 'clarification') {
    return null;
  }

  const handleChipClick = (chip) => {
    if (isReadOnly) return;
    // Block fax / email chips if parcels missing APN
    const blocked = blockedMessage(chip);
    if (blocked) {
      toast.error(blocked, { duration: 6000 });
      return;
    }
    const wasSelected = selectedChipIds.has(chip.id);
    const next = new Set(selectedChipIds);
    if (wasSelected) {
      next.delete(chip.id);
    } else {
      next.add(chip.id);
    }
    setSelectedChipIds(next);
    // Notify parent of chip selection changes for structured detection
    notifyChipIds(next);

    if (!wasSelected) {
      const resolved = applyTemplate(chip.template, effectiveJurisdictionData);
      const current = (reason || '').trimEnd();
      const updated = current ? `${current}\n\n${resolved}` : resolved;
      onReasonChange(updated);
    }
    if (clearValidationErrors) clearValidationErrors();
  };

  const handleTextChange = (e) => {
    onReasonChange(e.target.value);
    if (clearValidationErrors) clearValidationErrors();
  };

  const handleClearNote = () => {
    setSelectedChipIds(new Set());
    notifyChipIds(new Set());
    onReasonChange('');
  };

  const statusLabel = selectedStatus === 'pending' ? 'Pending' : 'Clarification';
  const sc = selectedStatus === 'pending'
    ? {
        bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
        chipBg: 'bg-amber-100', chipHover: 'hover:bg-amber-200',
        chipSelected: 'bg-amber-500 text-white', chipBorder: 'border-amber-300',
      }
    : {
        bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700',
        chipBg: 'bg-purple-100', chipHover: 'hover:bg-purple-200',
        chipSelected: 'bg-purple-500 text-white', chipBorder: 'border-purple-300',
      };

  const chipCount = selectedChipIds.size;

  // Deduplicate consecutive identical messages, keeping the most recent
  const previousNotes = (() => {
    const raw = (statusNotes || [])
      .filter(n => n.message && n.message.trim())
      .slice()
      .reverse();
    const deduped = [];
    for (const note of raw) {
      const prevMsg = deduped.length > 0 ? deduped[deduped.length - 1].message.trim() : null;
      if (prevMsg !== note.message.trim()) {
        deduped.push(note);
      }
    }
    return deduped;
  })();

  // Use centralized ET formatter from utils/formatters.js
  // It handles: missing Z suffix → appends Z → date-fns-tz converts UTC → ET
  const formatNoteDate = (dateStr) => {
    if (!dateStr) return '';
    const result = formatDateTimeShortET(dateStr);
    return result === 'N/A' || result === 'Invalid date' ? '' : result;
  };

  // Detect action type from message content for inline icon
  const getActionIcon = (message) => {
    const lower = (message || '').toLowerCase();
    if (lower.includes('fax')) return { icon: '📠', label: 'Fax' };
    if (lower.includes('call') || lower.includes('phone') || lower.includes('voicemail')) return { icon: '📞', label: 'Call' };
    if (lower.includes('email') || lower.includes('message') || lower.includes('mail')) return { icon: '✉️', label: 'Mail' };
    return null;
  };

  return (
    <div className="space-y-3 mt-3">
      {editMode && (
      <>
      {/* Quick Reason Chips */}
      <div className={`rounded-lg border ${sc.border} bg-white p-3`}>
        {/* Chips Row — no header, no labels */}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => {
            const isSelected = selectedChipIds.has(chip.id);
            const chipBlockedMsg = blockedMessage(chip);
            const isFaxBlocked = !!chipBlockedMsg;
            const isEmailBlocked = isEmailChip(chip) && isEmailChipBlocked;
            return (
              <div key={chip.id} className="relative group">
                <button
                  type="button"
                  onClick={() => handleChipClick(chip)}
                  disabled={isReadOnly}
                  className={`
                    px-2.5 py-1 rounded-md text-xs font-medium
                    border flex items-center gap-1
                    transition-all duration-150
                    ${isFaxBlocked
                      ? 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed'
                      : isSelected
                        ? `${sc.chipSelected} border-transparent shadow-sm`
                        : `bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300`
                    }
                    ${isReadOnly ? 'opacity-50 cursor-not-allowed' : isFaxBlocked ? '' : 'cursor-pointer'}
                  `}
                  data-testid={`quick-reason-chip-${chip.id}`}
                  data-automation={chip.automation || undefined}
                >
                  {isFaxBlocked && <AlertTriangle className="w-3 h-3 text-red-400" />}
                  {isSelected && !isFaxBlocked && <Check className="w-3 h-3" />}
                  {chip.label}
                </button>
                {isFaxBlocked && (
                  <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none text-center ${isEmailBlocked ? 'w-72 whitespace-normal' : 'whitespace-nowrap max-w-xs'}`} data-testid={isEmailBlocked ? 'email-chip-blocked-tooltip' : 'fax-chip-blocked-tooltip'}>
                    {chipBlockedMsg}
                  </div>
                )}
              </div>
            );
          })}
          {chips.length === 0 && chipsLoaded && (
            <span className="text-xs text-gray-400 py-0.5">No chips configured for {statusLabel}.</span>
          )}
          {(chipCount > 0 || (reason || '').trim()) && (
            <button
              type="button"
              onClick={handleClearNote}
              className="px-2 py-1 rounded-md text-xs font-medium text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 flex items-center gap-1 transition-all"
              title="Clear all quick reasons and the update text"
              data-testid="clear-chip-btn"
            >
              <X className="w-3 h-3" />
              Clear All{chipCount > 0 ? ` (${chipCount})` : ''}
            </button>
          )}
        </div>
      </div>

      {/* New Note Textarea */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1.5">
          <span>Add Update</span>
          <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal text-[11px]">(min 10 chars)</span>
        </label>
        <div className="relative">
          <textarea
            value={reason}
            onChange={handleTextChange}
            className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-150 resize-none text-sm ${
              validationError
                ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400'
                : 'border-gray-300 hover:border-gray-400'
            } ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            style={{ minHeight: '80px', height: '80px' }}
            placeholder="Add follow-up or update…"
            disabled={isReadOnly}
            minLength="10"
            maxLength="2000"
            data-testid="status-reason-textarea"
          />
          <div className="absolute bottom-2 right-2 pointer-events-none">
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              reason.length >= 10
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}>
              {reason.length}/2000
            </span>
          </div>
        </div>

        <div className="mt-1 flex items-center justify-between h-5">
          <div>
            {reason.length > 0 && reason.length < 10 && (
              <span className="text-[11px] text-gray-500">
                {10 - reason.length} more needed
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {reason.length > 0 && (
              <button
                type="button"
                onClick={handleClearNote}
                className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-0.5 transition-colors"
                data-testid="clear-note-btn"
              >
                <Trash2 className="w-2.5 h-2.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {validationError && (
          <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1.5 bg-red-50 px-2.5 py-1.5 rounded-md border border-red-200">
            <X className="w-3 h-3 flex-shrink-0" />
            <span>{validationError}</span>
          </div>
        )}
      </div>
      </>
      )}

      {/* Previous Notes — Collapsed by default */}
      {activeTab !== 'tax' && previousNotes.length > 0 && (
        <PreviousNotesSection notes={previousNotes} formatNoteDate={formatNoteDate} getActionIcon={getActionIcon} />
      )}
    </div>
  );
};

// Collapsed-by-default Previous Notes
const PreviousNotesSection = ({ notes, formatNoteDate, getActionIcon }) => {
  const [expanded, setExpanded] = useState(false);
  if (!notes.length) return null;

  return (
    <div data-testid="previous-notes-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-[11px] font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
        data-testid="toggle-previous-notes"
      >
        <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Previous Notes ({notes.length})
      </button>
      {expanded && (
        <div className="space-y-1 mt-1.5 max-h-[200px] overflow-y-auto pr-1">
          {notes.map((note, i) => {
            const isLatest = i === 0;
            const typeLabel = note.type === 'pending' ? 'PENDING' 
              : note.type === 'clarification' ? 'CLARIFICATION' 
              : note.type === 'mail_away' ? 'MAIL-AWAY' 
              : (note.type || '').toUpperCase();
            const typeCls = note.type === 'pending' ? 'text-amber-700 font-bold' 
              : note.type === 'clarification' ? 'text-purple-700 font-bold' 
              : 'text-gray-600 font-bold';
            const actionInfo = getActionIcon(note.message);

            return (
              <div 
                key={note.created_at || i} 
                className={`rounded-md px-2.5 py-1.5 border ${
                  isLatest ? 'bg-green-50/60 border-green-200' : 'bg-gray-50 border-gray-100'
                }`}
                data-testid={`previous-note-${i}`}
              >
                <div className="flex items-center gap-1 text-[10px] leading-tight mb-0.5 flex-wrap">
                  {typeLabel && <span className={typeCls}>{typeLabel}</span>}
                  {isLatest && <span className="text-[9px] font-semibold uppercase bg-green-100 text-green-700 px-1 py-0.5 rounded" data-testid="latest-note-tag">Latest</span>}
                  <span className="text-gray-300">&middot;</span>
                  {note.created_by_name && <span className="text-gray-500">{note.created_by_name}</span>}
                  {note.created_at && (
                    <>
                      <span className="text-gray-300">&middot;</span>
                      <span className="text-gray-400">{formatNoteDate(note.created_at)}</span>
                    </>
                  )}
                </div>
                <div className="flex items-start gap-1">
                  {actionInfo && <span className="flex-shrink-0 text-xs leading-4" title={actionInfo.label}>{actionInfo.icon}</span>}
                  <p className="text-xs text-gray-700 leading-4">{note.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusReasonField;
